import React, { useEffect, useState } from 'react';
import {
  ListItem,
  ListItemAvatar,
  Avatar,
  Typography,
  Box,
  Chip,
} from '@mui/material';
import { formatChatDate } from 'components/helpers/commonFunctions/ChatDateTimeCalculation';
import { customerChatsConstants } from 'constants/constantText';
import PdfIcon from 'assets/images/PdfIcon.svg';
import Logo from 'assets/images/Logo100X132.png';
import { type ChatMessagesAttributesType } from 'types/services';
import { getResource } from 'service/UserService';

interface Props {
  message: ChatMessagesAttributesType;
  index: number;
  profileImage: string;
  name: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  colors: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  customStyles: any;
  showAdditionalFields: boolean[];
  setShowAdditionalFields: React.Dispatch<React.SetStateAction<boolean[]>>;
  setOpenImageDialog: React.Dispatch<React.SetStateAction<boolean>>;
  setViewImage: React.Dispatch<React.SetStateAction<string | null>>;
  setOpenComplaintDialog: React.Dispatch<React.SetStateAction<boolean>>;
  setChatDescription: React.Dispatch<React.SetStateAction<string>>;
  setChatDetails: React.Dispatch<
    React.SetStateAction<ChatMessagesAttributesType>
  >;
}

const ChatMessageItem = ({
  message,
  index,
  profileImage,
  name,
  colors,
  customStyles,
  showAdditionalFields,
  setShowAdditionalFields,
  setOpenImageDialog,
  setViewImage,
  setOpenComplaintDialog,
  setChatDescription,
  setChatDetails,
}: Props): JSX.Element => {
  const isEmployee = message.senderType === customerChatsConstants.employee;
  const [url, setUrl] = useState(null);

  const getUrl = async (key: string): void => {
    const data = await getResource(key);
    if (data.url) {
      setUrl(data.url);
    }
  };

  useEffect(() => {
    if (!message.imageUri || message?.fileType === 'application/pdf') return;
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    getUrl(message.imageUri);
  }, [message.imageUri]);

  return (
    <ListItem
      sx={[
        customStyles.commonFlexStyle,
        {
          justifyContent: isEmployee ? 'flex-end' : 'flex-start',
        },
      ]}
    >
      <Box
        sx={[
          customStyles.commonFlexStyle,
          { flexDirection: isEmployee ? 'row-reverse' : 'row' },
        ]}
      >
        <ListItemAvatar>
          <Box
            sx={[
              customStyles.msgAvatarBoxStyle,
              { marginLeft: isEmployee && 4 },
            ]}
          >
            <Avatar
              sx={[
                customStyles.msgAvatarStyle,
                {
                  bgcolor: isEmployee && colors.greenAccent[500],
                },
              ]}
            >
              {isEmployee ? (
                <img
                  src={Logo}
                  alt="Zen Pensions logo"
                  style={customStyles.logoImageStyle}
                />
              ) : profileImage ? (
                <img
                  src={profileImage}
                  alt={name}
                  style={customStyles.profileImageStyle}
                />
              ) : (
                name.slice(0, 1)
              )}
            </Avatar>
          </Box>
        </ListItemAvatar>

        <Box sx={customStyles.msgBoxStyle}>
          <Box
            sx={[
              customStyles.subMsgBoxStyle,
              {
                backgroundColor: isEmployee
                  ? colors.teaGreen[500]
                  : colors.turquoiseBlue[500],
              },
            ]}
          >
            <Typography sx={customStyles.msgTypoStyle}>
              {message.message}
            </Typography>

            <Box
              onClick={async () => {
                const res: { url: string } = await getResource(
                  message.imageUri,
                );

                if (!res?.url) return;
                if (message?.fileType === 'application/pdf') {
                  const link = document.createElement('a');
                  link.href = res.url;
                  link.download = `download.pdf`;
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                } else {
                  setOpenImageDialog(true);
                  setViewImage(res.url);
                }
              }}
              sx={customStyles.cursorStyle}
            >
              {
                <>
                  {message?.fileType === 'application/pdf' ? (
                    <img
                      style={customStyles.pdfIconStyle}
                      src={PdfIcon}
                      alt="PDF"
                    />
                  ) : url ? (
                    <img
                      src={url}
                      style={customStyles.maxWidthStyle}
                      alt={url}
                    />
                  ) : null}
                </>
              }
            </Box>

            {/* {message.senderType === customerChatsConstants.customer && (
              <Box
                sx={customStyles.showBoxStyle}
                onClick={() => {
                  const updatedFields = [...showAdditionalFields];
                  updatedFields[index] = !updatedFields[index];
                  setShowAdditionalFields(updatedFields);
                }}
              >
                <Typography sx={customStyles.typoColorStyle}>...</Typography>
              </Box>
            )} */}

            {showAdditionalFields[index] && (
              <Box sx={customStyles.chipBoxStyle}>
                <Chip
                  label="Make a Complaint"
                  color="success"
                  onClick={() => {
                    setOpenComplaintDialog(true);
                    setChatDescription(message.message);
                    setChatDetails(message);
                  }}
                />
              </Box>
            )}
          </Box>

          <Box sx={customStyles.timeMainBoxStyle}>
            <Box
              sx={[
                customStyles.commonFlexStyle,
                {
                  justifyContent: isEmployee ? 'flex-end' : 'flex-start',
                },
              ]}
            >
              <Typography
                color={colors.secondaryGreen[200]}
                sx={customStyles.timestampStyle}
              >
                {message?.createdAt && formatChatDate(message.createdAt)}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </ListItem>
  );
};

export default ChatMessageItem;
